import React, { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isIE } from "react-device-detect";
import CustomProperty from "../../custom-property/newCP";
import Select from "../../select/Select";
import Validator from "../../validator/Validator";
import { ADVANCE_APPLICATION_PROPETIES } from "infrastructure/enum/object-properties.enum";
import EmployeeAutocomplete from "../../autocomplete/EmployeeAutocomplete";
import { RoadComponent } from "./components/RoadComponent";
import { ApplicationTypeComponent } from "./components/ApplicationTypeComponent";
import { Label } from "./components/Label";
import { CreateDocumentModalProps } from "./types";
import { generateClassName } from "./utils";
import { availableCustomProperties } from "services/ApiService";

export interface FilterCustomProperties {
  id: number;
  name: {
    en: string;
    ru: string;
  };
}

const CreateDocumentModal: React.FC<CreateDocumentModalProps> = ({
  editMode,
  isSelectOccupation,
  creatorOccupationOptions,
  expenseTypes,
  expenseType,
  typeRadioClickHandler,
  expenseTypeCode,
  roads,
  changeRoads,
  changeRoadMode,
  multiRoadMode,
  isTabIndexDisabled,
  assistanceOptions,
  isAssistantManagerOccupation,
  isCoordinatorOccupation,
  assistanceValidatorRef,
  assistanceRef,
  assistance,
  assistanceChooseHandler,
  props,
  responsibleValidatorRef,
  responsibleChooseHandler,
  employeeAutocompleteReadOnly,
  responsibleOccupationOptions,
  responsibleOccupationRef,
  responsibleOccupation,
  responsibleOccupationChooseHandler,
  businessGoal,
  businessGoalValidatorRef,
  hideBusinessGoal,
  businessGoalRef,
  businessGoalOnlyOne,
  businessGoalOptions,
  businessGoalChooseHandler,
  descriptionRef,
  description,
  changeDescription,
  dataRef,
  editPermissions,
  createPermissions,
  readPermissions,
  assigneeDetail,
  aoProps,
  showTime,
  expenseProps,
}) => {
  const { t, i18n } = useTranslation();
  const isExpense = !!expenseProps;
  const permissions = !!props.editId ? editPermissions : createPermissions;

  const [filterCustomProperties, setFilterCustomProperties] = useState<
    FilterCustomProperties[] | null
  >(null);

  const getAvailableCustomProperties = async () => {
    setFilterCustomProperties(null);
    if (businessGoal?.value) {
      const data = await availableCustomProperties(Number(businessGoal.value));
      setFilterCustomProperties(data.data.customProperties);
    }
  };

  useEffect(() => {
    if (businessGoalOptions.length > 0 && businessGoal) {
      if (businessGoal?.value) {
        getAvailableCustomProperties();
      }
    }
  }, [businessGoal?.value, businessGoalOptions?.length]);

  const { rights, bookerAndOwner, applicationForExpenseDetail } =
    expenseProps || {};
  const { responsible, advanceReportDetail } = aoProps || {};

  const generateClassNames = (property: string) => {
    return generateClassName(
      readPermissions,
      createPermissions,
      editPermissions,
      editMode,
      property
    );
  };

  const showRoadComponent =
    expenseTypeCode === "Travel" &&
    (isExpense ||
      createPermissions.includes(
        ADVANCE_APPLICATION_PROPETIES.BUSINESS_TRIP_OPTION
      ) ||
      editPermissions.includes(
        ADVANCE_APPLICATION_PROPETIES.BUSINESS_TRIP_OPTION
      ));

  const showAssistanceComponent =
    assistanceOptions.length > 0 &&
    !isAssistantManagerOccupation() &&
    !isCoordinatorOccupation();

  return (
    <>
      {(editMode ||
        isSelectOccupation ||
        creatorOccupationOptions.length < 1) && (
        <>
          <div className={`box-modal-form-block`}>
            {/*Тип заявки*/}
            <ApplicationTypeComponent
              isExpense={isExpense}
              expenseTypes={expenseTypes}
              expenseType={expenseType}
              typeRadioClickHandler={typeRadioClickHandler}
              editMode={editMode}
              editPermissions={editPermissions}
            />
          </div>
          <div className="box-modal-form-block">
            <div className="title">
              {t(
                isExpense
                  ? "create_expense.application_details"
                  : "modals.create_advance_report_modal.application_details"
              )}
            </div>
            {showRoadComponent && (
              <RoadComponent
                showTime={showTime}
                isExpense={isExpense}
                roads={roads}
                rights={expenseProps?.rights}
                changeRoads={changeRoads}
                changeRoadMode={changeRoadMode}
                multiRoadMode={multiRoadMode}
                isTabIndexDisabled={isTabIndexDisabled}
                generateClassName={generateClassNames}
              />
            )}
            <div className="box-modal-form-block" style={{ marginBottom: 0 }}>
              {showAssistanceComponent && (
                <Validator type={"select"} ref={assistanceValidatorRef}>
                  <>
                    <Label
                      label="create_expense.assignee_employee"
                      required={true}
                    />
                    <Select
                      ref={assistanceRef}
                      defaultValue={assistance}
                      options={assistanceOptions}
                      onChoose={assistanceChooseHandler}
                      defaultText={t(
                        "create_expense.placeholder.select_employee"
                      )}
                      disabled={!!props.editId}
                      style={{ marginRight: "-12px" }}
                    />
                  </>
                </Validator>
              )}
              {isCoordinatorOccupation() && (
                <Validator
                  type={"text"}
                  ref={responsibleValidatorRef}
                  isRoad={isIE}
                >
                  <>
                    <Label
                      label="create_expense.assignee_employee"
                      required={true}
                    />
                    <div
                      className={`input-item${
                        bookerAndOwner ? " disabled" : " "
                      }`}
                      style={{ marginBottom: "0px" }}
                    >
                      <div
                        className="input-search"
                        style={{ marginRight: "-12px" }}
                      >
                        <EmployeeAutocomplete
                          placeholder={t(
                            "create_expense.placeholder.enter_employee"
                          )}
                          companyId={
                            props.editId
                              ? (isExpense
                                  ? applicationForExpenseDetail
                                  : advanceReportDetail
                                )?.company?.id
                              : assigneeDetail.companyId
                          }
                          isTechnicalOccupationHidden
                          onChoose={responsibleChooseHandler}
                          defaultText={isExpense ? "" : responsible?.text}
                          readOnly={employeeAutocompleteReadOnly}
                          tabIndex={isTabIndexDisabled ? -1 : 0}
                        />
                      </div>
                    </div>
                  </>
                </Validator>
              )}
              {responsibleOccupationOptions.length > 0 && (
                <Validator type={"select"} ref={responsibleOccupationRef}>
                  <>
                    <Label
                      label="create_expense.assignee_employee_occupation"
                      required={true}
                    />
                    <Select
                      ref={responsibleOccupationRef}
                      defaultValue={responsibleOccupation}
                      options={responsibleOccupationOptions}
                      onChoose={responsibleOccupationChooseHandler}
                      defaultText={t(
                        "create_expense.placeholder.select_employee"
                      )}
                      disabled={!!props.editId}
                      style={{ marginRight: "-12px" }}
                    />
                  </>
                </Validator>
              )}
              {/*Бизнес цель*/}
              {businessGoalOptions.length > 0 && (
                <Validator
                  type={"select"}
                  ref={businessGoalValidatorRef}
                  errorMessage={t("create_expense.select_business_goal")}
                  style={{ display: hideBusinessGoal ? "none" : "block" }}
                >
                  <>
                    <Label
                      label="create_expense.business_goal"
                      required={true}
                    />
                    <Select
                      disabled={isExpense && !rights?.businessTrip}
                      ref={businessGoalRef}
                      defaultValue={
                        editMode || businessGoalOnlyOne ? businessGoal : null
                      }
                      options={businessGoalOptions.map((businessGoalOption) => {
                        return {
                          value: businessGoalOption.id,
                          text: businessGoalOption.name[
                            i18n.language as "ru" | "en"
                          ],
                        };
                      })}
                      onChoose={businessGoalChooseHandler}
                      defaultText={t(
                        "modals.create_advance_report_modal.enter_business_goal"
                      )}
                      style={{ marginRight: "-12px" }}
                    />
                  </>
                </Validator>
              )}
              <div className={`box-modal-form-block`}>
                <div className="input-item">
                  <Label label="create_expense.description" required={false} />
                  <input
                    className={`input`}
                    type="text"
                    ref={descriptionRef}
                    value={description}
                    maxLength={400}
                    onChange={changeDescription}
                    placeholder={t(
                      isExpense
                        ? "create_expense.placeholder.description"
                        : "modals.create_advance_report_modal.enter_description"
                    )}
                    tabIndex={isTabIndexDisabled ? -1 : 0}
                  />
                </div>
              </div>
            </div>
              <CustomProperty
                businessGoal={businessGoal?.value}
                availableProperties={filterCustomProperties}
                dataExpense={dataRef.current}
                type={
                  isExpense ? "ExpenseApplication" : "AdvanceReportApplication"
                }
                id={props.editId}
                isEdit={!!props.editId}
                isZNR={isExpense}
                isAO={!isExpense}
                permissions={permissions}
                occupationsId={assigneeDetail.occupationId}
                companyId={
                  props.editId
                    ? (isExpense
                        ? applicationForExpenseDetail
                        : advanceReportDetail
                      )?.company?.id
                    : assigneeDetail.companyId
                }
                tabIndex={isTabIndexDisabled ? -1 : 0}
              />
          </div>
        </>
      )}
    </>
  );
};

export default memo(CreateDocumentModal);
